/**
 * Vincent B1019 (Eric 2026-02-26 SPEC STRICK PROD) — Auto-slow polling
 * quand Chrome signale la saturation navigateur.
 *
 * Écoute l'event `knoria:browser-throttled` (émis par AuthContext quand
 * une requête échoue avec `ERR_INSUFFICIENT_RESOURCES` et variantes).
 * Retourne un multiplicateur d'intervalle applicable aux polling hooks :
 *
 *     - 1  (normal) par défaut
 *     - 5  (throttled) pendant les 30 secondes qui suivent un event
 *
 * Effet : un poll de 3s passe automatiquement à 15s tant que le
 * navigateur est saturé, permettant au pool de sockets de se libérer.
 * Aucun action utilisateur requise — le multiplicateur revient à 1
 * après 30s sans nouvel event.
 *
 * Usage dans un hook polling :
 *
 * ```js
 * const throttleMultiplier = useBrowserThrottle();
 * const effectiveInterval = BASE_INTERVAL_MS * throttleMultiplier;
 * ```
 */
import { useEffect, useState } from "react";

const RESET_MS = 30_000;
const THROTTLE_MULTIPLIER = 5;
const NORMAL_MULTIPLIER = 1;

export default function useBrowserThrottle() {
  const [multiplier, setMultiplier] = useState(NORMAL_MULTIPLIER);

  useEffect(() => {
    let resetTimer = null;
    const onThrottled = () => {
      setMultiplier(THROTTLE_MULTIPLIER);
      if (resetTimer) clearTimeout(resetTimer);
      resetTimer = setTimeout(() => {
        setMultiplier(NORMAL_MULTIPLIER);
      }, RESET_MS);
    };
    window.addEventListener("knoria:browser-throttled", onThrottled);
    return () => {
      window.removeEventListener("knoria:browser-throttled", onThrottled);
      if (resetTimer) clearTimeout(resetTimer);
    };
  }, []);

  return multiplier;
}
